import { renderer } from '../Renderer';
import { ViewFactory } from '../Template';
import AttributeExpression from './AttributeExpression';
import DataAttributeExpression from './DataAttributeExpression';
import Expression, { snakeToCamel, hasExpression } from './Expression';
import DataExpression from './DataExpression';
import { Observable, observable } from '../../signal/Signal';

class ViewExpression extends Expression {

	target:Element;
	attributes:{[key:string]:string};
	expressions:Expression[];
	viewFactory:ViewFactory;

	constructor( target:Element, viewFactory:ViewFactory ) {
		super();
		this.target = target;
		this.viewFactory = viewFactory;
		this.expressions = [];
		this.parseAttributes();
		this.template = this.attributes['view'];
	}

	update( data:any, attribute?:string ) {

		// check if we have an expression update of the view attribute
		if( super.hasAttribute( attribute ) ) {

            // make sure node is empty
            this.target.innerHTML = '';
            this.expressions = [];

			this.renderView( data );
		} else {
			// update expressions only
			this.expressions.forEach( exp => {
				if( exp.hasAttribute( attribute ) ) exp.update( data, attribute );
			});
		}
	}

	renderView( data ) {

		let node = this.target;

		let viewName = renderer.render( this.attributes['view'], data );
		let view = this.viewFactory( viewName );

		// set data if there is any
		if( node.hasAttribute( 'data' ) ) {
			let dataExp = new DataExpression( view, node.getAttribute( 'data' ) );
			view.data = dataExp.value( data );
            this.expressions.push( dataExp );

            // remove node attribute
			node.removeAttribute( 'data' );
		}

		node['_lnview'] = view;

		// handle view attributes
		Object.keys( this.attributes ).forEach( name => {

			// ignore special view attribute
			if( name == 'view' || name == 'data' ) return;

			let value = this.attributes[ name ];

			// handle events
			if( name.indexOf( 'on-' ) == 0 ) {
				// get signal name an subscribe
				let signalName = snakeToCamel( name.replace( 'on-', '' ) );

				// look on data for signal
				if( view.data[ signalName ] instanceof Observable ) {
					subscribeTo( view.data[ signalName ], value, data );
				// otherwise register on node signal
				} else {
					subscribeTo( observable( signalName, node ), value, data );
				}

				node.removeAttribute( name );
				return;
			}


			let propName = snakeToCamel( name );

			// check if viewModel has property otherwise its a node attribute
			if( view.data instanceof Object && propName in view.data ) {

				let dataExp = new DataAttributeExpression( view.data, propName, value );
				dataExp.update( data, null );
				this.expressions.push( dataExp );

				// remove node attribute
				node.removeAttribute( name );

			} else if( hasExpression( node.getAttribute( name ) ) ) {
				let expr = new AttributeExpression( node, name );
				expr.update( data );
				this.expressions.push( expr );
			}
		});

		view.render( node );
	}

	parseAttributes() {
		this.attributes = {};
		for (let i = 0; i < this.target.attributes.length; i++) {
			const attr = this.target.attributes[i];
			this.attributes[ attr.name ] = attr.value;
		}
	}

	/**
	 * Checks if the view="..." expression and all its sub expression have the attribute to update
	 * @param name The name of the attribute to check if its in expressions
	 */
	hasAttribute( name?:string ) {
		if( super.hasAttribute( name ) ) return true;
		return this.expressions.some( expr => expr.hasAttribute( name ) );
	}
}

export default ViewExpression;


/**
 * Helper function to subscribe to signals
 * @param observable the observable to subscribe
 * @param content the string content of the on-... attribute for the oberserver callback
 * @param data the data/viewModel to look callback functions
 */
export function subscribeTo( observable:Observable<any>, content:string, data:{} ) {
	// check for valid observable
	if( !observable ) return;

	// event closure
	if( hasExpression( content ) ) {

		let callback = renderer.compile( content, 'event,data' );

		observable.subscribe( ( event ) => {
			callback.call( renderer.context, event, data );
		});

	// viewModel function
	} else if( data[ content ] ) {
		observable.subscribe( data[ content ].bind( data ) );
	}
}
